/**
 * PopupBidirectionalMapChart - Stateless version of BidirectionalMapChart for popup windows
 *
 * Receives track data from props instead of fetching via API.
 * Used in PopupVesselTimelinePage.
 */
import React, { useCallback, useMemo, useState, useRef } from 'react';
import { DateRange } from 'react-day-picker';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DateTimePickerWithRange,
  LoadingSpinner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ais-ui/ui-core';
import { useTimeZoneContext } from '@ais-ui/utils';
import {
  ShipTrackMap,
  ShipTrackChart,
} from '../Targets/TargetDashboard/BidirectionalMapChart';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { PopupBaseMap } from './PopupBaseMap';

const MAX_CHART_POINTS = 50;

interface ShipTrackData {
  coordinates: (string | number)[][];
}

interface PopupBidirectionalMapChartProps {
  shipTrack: ShipTrackData | null;
  dateRange?: DateRange;
  onDateRangeChange?: (range: DateRange | undefined) => void;
  isLoading?: boolean;
  /** Unique key for chart data - when changed, forces chart components to re-render */
  chartKey?: string;
}

// Simple error boundary for chart/map components
interface ChartErrorBoundaryState {
  hasError: boolean;
}

class ChartErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  ChartErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode; fallback: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ChartErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error(
      '[PopupBidirectionalMapChart] Chart/Map error:',
      error,
      errorInfo,
    );
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

// Fallback component for chart errors
const ChartErrorFallback = () => (
  <div className="h-[400px] w-full flex items-center justify-center text-muted-foreground">
    Failed to load chart. Track data may be unavailable.
  </div>
);

// Fallback component for map errors
const MapErrorFallback = () => (
  <div className="h-[500px] w-full flex items-center justify-center text-muted-foreground rounded-lg bg-muted/20">
    Failed to load map. Track data may be unavailable.
  </div>
);

export const PopupBidirectionalMapChart = ({
  shipTrack,
  dateRange,
  onDateRangeChange,
  isLoading = false,
  chartKey,
}: PopupBidirectionalMapChartProps) => {
  const [selectedValue, setSelectedValue] = useState<string>('sog');
  const [hoveredPointIndex, setHoveredPointIndex] = useState<number | null>(
    null,
  );
  const { timeZone } = useTimeZoneContext();
  const { userDateFormat } = useDateTimeFormat();

  // Keep reference to last valid track data to show while loading
  const lastValidTrackRef = useRef<ShipTrackData | null>(null);

  // Handle hover synchronization between map and chart
  const handlePointHover = useCallback((index: number | null) => {
    setHoveredPointIndex(index);
  }, []);

  // Convert track data to the format expected by chart/map components
  // Use last valid data while loading to prevent flickering
  // Include chartKey in deps to ensure new data is processed when date range changes
  const trackData = useMemo(() => {
    const sourceData =
      shipTrack ?? (isLoading ? lastValidTrackRef.current : null);

    if (!sourceData?.coordinates || sourceData.coordinates.length === 0) {
      return null;
    }

    // Update the ref with valid data
    if (shipTrack?.coordinates && shipTrack.coordinates.length > 0) {
      lastValidTrackRef.current = shipTrack;
    }

    // Sample points if there are too many (keep max ~50 points for popup performance)
    const maxPoints = MAX_CHART_POINTS;
    let sampledCoordinates = sourceData.coordinates;
    if (sourceData.coordinates.length > maxPoints) {
      const step = Math.ceil(sourceData.coordinates.length / maxPoints);
      sampledCoordinates = sourceData.coordinates.filter(
        (_, index) => index % step === 0,
      );
      // Always include the last point if it wasn't already included by the sampling step
      if ((sourceData.coordinates.length - 1) % step !== 0) {
        sampledCoordinates.push(
          sourceData.coordinates[sourceData.coordinates.length - 1],
        );
      }
    }

    // Return HistoricalTrack-like object that ShipTrackChart/ShipTrackMap expect
    // Cast coordinates: runtime values are numbers + ISO string timestamp,
    // matching the portserver API behavior (typed as string[][] but actually mixed)
    return {
      ship: { imo: 0 },
      track: {
        coordinates: sampledCoordinates as unknown as string[][],
        type: 'LineString' as const,
        extensions: ['timestamp', 'cog', 'sog'],
      },
    };
    // Note: chartKey in deps forces re-computation when date range changes,
    // even if the coordinates happen to be similar
  }, [shipTrack, isLoading, chartKey]);

  // Only show date picker if callback is provided
  const showDatePicker = !!onDateRangeChange;

  // Wrap callback to match DateTimePickerWithRange's setDate type
  const handleSetDate: React.Dispatch<
    React.SetStateAction<DateRange | undefined>
  > = useCallback(
    (value) => {
      if (!onDateRangeChange) return;
      if (typeof value === 'function') {
        onDateRangeChange(value(dateRange));
      } else {
        onDateRangeChange(value);
      }
    },
    [onDateRangeChange, dateRange],
  );

  return (
    <Card className="col-span-4 p-4">
      <CardHeader className="space-y-8">
        <CardTitle>Vessel AIS</CardTitle>
        <div className="flex gap-4 items-center">
          <Select value={selectedValue} onValueChange={setSelectedValue}>
            <SelectTrigger className="w-[240px] text-md">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sog">Speed Over Ground</SelectItem>
              <SelectItem value="cog">Course Over Ground</SelectItem>
            </SelectContent>
          </Select>
          {showDatePicker && (
            <div className="w-96">
              <DateTimePickerWithRange
                date={dateRange}
                setDate={handleSetDate}
                timeZone={timeZone}
                dateTimeFormat={userDateFormat}
                dateLimit="past"
                defaultToTime="now"
                showPresets
              />
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="pl-2 relative">
        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-background/50 z-10 flex items-center justify-center">
            <LoadingSpinner className="h-8 w-8" />
          </div>
        )}
        {!trackData ? (
          <div className="h-[400px] w-full flex items-center justify-center text-muted-foreground">
            {isLoading
              ? 'Loading track data...'
              : 'No AIS track data available for selected period'}
          </div>
        ) : (
          <div className="grid grid-cols-3 grid-rows-2 gap-4">
            <ChartErrorBoundary fallback={<ChartErrorFallback />}>
              <ShipTrackChart
                trackData={trackData}
                hoveredPointIndex={hoveredPointIndex}
                onPointHover={handlePointHover}
                className="row-end-2 col-span-3"
                dataType={selectedValue as 'sog' | 'cog'}
              />
            </ChartErrorBoundary>
            <ChartErrorBoundary fallback={<MapErrorFallback />}>
              <ShipTrackMap
                trackData={trackData}
                hoveredPointIndex={hoveredPointIndex}
                onPointHover={handlePointHover}
                className="row-start-2 row-end-3 col-span-3"
                dataType={selectedValue as 'sog' | 'cog'}
                BaseMapComponent={PopupBaseMap}
              />
            </ChartErrorBoundary>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
